import { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, PoundSterling, Sparkles, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SectionHead } from "./SectionHead";

const REASONS = [
  { icon: ClipboardCheck, title: "Carefully described product conditions", text: "New, Open Box, Like New or Used: you'll know exactly what you're getting.", bg: "#D9F5E8", fg: "#168A5B" },
  { icon: PoundSterling, title: "Great-value finds", text: "Everyday bits and bobs at prices that won't break the bank.", bg: "#FFE5D1", fg: "#D9580F" },
  { icon: Sparkles, title: "New discoveries regularly", text: "Fresh stock and one-off items land often, so it's always worth a look.", bg: "#ECE4FF", fg: "#5B3FD1" },
  { icon: MapPin, title: "Straightforward UK shopping", text: "Clear prices in pounds and a simple, no-nonsense way to shop.", bg: "#DCEEFF", fg: "#1F6FD1" },
];

export const WhyShopaBee = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="why-section">
    <SectionHead eyebrow="Discover Something Worth Buying." title="Why ShopaBee?" />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {REASONS.map(({ icon: Icon, title, text, bg, fg }, i) => (
        <div key={title} className="rounded-2xl p-5" style={{ background: bg }} data-testid={`why-item-${i}`}>
          <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center" style={{ color: fg }}><Icon size={22} strokeWidth={2.2} /></span>
          <h3 className="mt-4 font-display font-semibold text-[#202A35]">{title}</h3>
          <p className="mt-2 text-sm text-[#202A35]/65 leading-relaxed">{text}</p>
        </div>
      ))}
    </div>
  </section>
);

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email: email.trim(), website: e.target.website.value });
      setDone(true);
      setEmail("");
    } catch (err) { toast.error(err?.response?.status === 422 ? "Please enter a valid email address." : apiError(err)); }
    setBusy(false);
  };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="newsletter-section">
      <div className="bg-[#FFD23F] rounded-3xl px-6 py-10 sm:px-12 sm:py-12 grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#202A35] tracking-tight">Be First to Find Out.</h2>
          <p className="mt-3 text-[#202A35]/70">New finds, fresh deals and unexpected bargains. Straight to your inbox.</p>
        </div>
        {done ? (
          <p className="font-semibold text-[#202A35]" data-testid="newsletter-success">You're signed up. Watch your inbox for new finds and deals. Every email has a one-click unsubscribe link.</p>
        ) : (
        <form onSubmit={submit} data-testid="newsletter-form">
          <div className="flex flex-col sm:flex-row gap-3">
            <input type="email" required maxLength={200} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
              aria-label="Email address" data-testid="newsletter-email-input"
              className="flex-1 h-12 px-5 rounded-full bg-white border border-[#202A35]/15 text-sm focus:outline-none focus:border-[#E2A100]" />
            <input name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" defaultValue="" />
            <button type="submit" disabled={busy} data-testid="newsletter-subscribe-btn"
              className="h-12 px-7 rounded-full bg-[#202A35] text-white text-sm font-semibold hover:bg-[#111821] transition-colors disabled:opacity-60">
              {busy ? "Signing up…" : "Subscribe"}
            </button>
          </div>
          <p className="mt-3 text-xs text-[#202A35]/70" data-testid="newsletter-consent-text">
            By subscribing you agree to receive occasional emails from ShopaBee about new finds, deals and news. Unsubscribe any time with one click. See our <Link to="/info/privacy-policy" className="underline">Privacy Policy</Link>.
          </p>
        </form>
        )}
      </div>
    </section>
  );
};
